import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useQuery } from '@tanstack/react-query';

import { getClients } from 'api/client';

import {
  processSelectedAll,
  processRewSelect,
  processClientesForReport,
  processClientesForDocumentary,
  getBrokerageHouses,
} from 'views/Reports/helpers';

import { setChipData, getChipData } from 'store/clients';

import { KEY_FILTER_OPTIONS } from 'components/Clients/Filters/constants';

import { arrayValidation } from 'utils/array';

import { QUERY_STALE_TIME_DEFAULT } from 'constants/globals';
import { ERROR_INFO } from 'constants/messages';

import type { Client } from 'api/client/models';

import useSnackbar from './useSnackbar';
import useGlobalApp from './useGlobalApp';

const STATUS_ACTIVE = '1';

const fetchAllClients = async (
  onProgress?: (current: number, total?: number) => void,
): Promise<Client[]> => {
  const limit = 500;
  let page = 1;
  let allRows: Client[] = [];
  let total = 0;
  let loadMoreRecords = true;

  while (loadMoreRecords) {
    const response = await getClients({
      page,
      pageSize: limit,
      order: 'name',
      filterBy: 'NEW_WAY',
    });

    if (
      !response.success ||
      !response.data ||
      !arrayValidation(response.data?.data)
    ) {
      throw new Error('CLIENT_FETCH_ERROR');
    }

    const { data: rows, pagination } = response.data;

    allRows = [...allRows, ...rows];
    total = pagination?.total || allRows.length;

    onProgress?.(allRows.length, total);

    if (allRows.length >= total) loadMoreRecords = false;

    page += 1;
  }

  return allRows;
};

const useReports = (isDocumentary = false) => {
  const { showSnackbarInfo, showSnackbarError } = useSnackbar();
  const dispatch = useDispatch();
  const { isClientRole } = useGlobalApp();
  const [loading, setLoading] = useState<boolean>(false);
  const [tableRows, setTableRows] = useState<Client[]>([]);
  const [originalRows, setOriginalRows] = useState<Client[]>([]);
  const [isSelectedAll, setSelectedAll] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [recordsToDownload, setRecordsToDownload] = useState<number>(0);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const chipData = useSelector(getChipData);

  const { data: layouts = [] } = useQuery({
    queryKey: ['brokerageHouses'],
    queryFn: getBrokerageHouses,
    staleTime: QUERY_STALE_TIME_DEFAULT,
  });

  const {
    data,
    isLoading: isLoadingClients,
    isError,
  } = useQuery({
    queryKey: ['clientsAll'],
    queryFn: () =>
      fetchAllClients((current, total) => {
        if (isMountedRef.current) {
          showSnackbarInfo(
            `Consultando ${current} registros${total ? ` de ${total}` : ''}`,
          );
        }
      }),
    enabled: !isClientRole,
    staleTime: QUERY_STALE_TIME_DEFAULT,
  });

  function handleRowSelect(
    e: React.ChangeEvent<HTMLInputElement>,
    newValue: boolean,
    row: Client,
  ) {
    setTableRows(processRewSelect(tableRows, row, newValue));
    setOriginalRows(processRewSelect(originalRows, row, newValue));
  }

  const handreSelectedAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectdAll = event.target.checked;
    setTableRows(processSelectedAll(tableRows, selectdAll));

    const newOriginalRows = originalRows.map((item) => {
      const option = tableRows.find((row) => row.clientId === item.clientId);
      if (option) {
        return {
          ...item,
          selected: selectdAll,
        };
      }
      return item;
    });
    setOriginalRows([...newOriginalRows]);
    setSelectedAll(selectdAll);
  };

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // setValueSearch(newValue);
    const selected = {
      label: newValue || '',
      value: newValue,
    };
    dispatch(
      setChipData([
        ...chipData,
        { ...selected, filterBy: KEY_FILTER_OPTIONS.client },
      ]),
    );
  };

  useEffect(() => {
    if (isError) {
      showSnackbarError(ERROR_INFO);
    }
  }, [isError, showSnackbarError]);

  useEffect(() => {
    if (arrayValidation(data)) {
      const newRows = data?.filter(
        (newRow) => newRow.cveStatus === STATUS_ACTIVE,
      );
      const mapped = isDocumentary
        ? processClientesForDocumentary(newRows || [])
        : processClientesForReport(newRows || [], layouts);
      setOriginalRows(mapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, layouts]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRows]);

  useEffect(() => {
    if (!arrayValidation(originalRows)) return;
    const tableRowsLengthSelectd =
      originalRows.filter((row) => row.selected)?.length || 0;

    setRecordsToDownload(tableRowsLengthSelectd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalRows]);

  return {
    layouts,
    loading: isLoadingClients || loading,
    tableRows,
    originalRows,
    isSelectedAll,
    page,
    pageSize,
    showLoader,
    recordsToDownload,
    handreSelectedAll,
    setShowLoader,
    setOriginalRows,
    setTableRows,
    setLoading,
    setPage,
    handleRowSelect,
    setPageSize,
    handleChangeSearch,
  };
};

export default useReports;
